import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Swords, RefreshCw, MapPin, Filter as FilterIcon, Maximize2, Save, Check, AlertCircle, Play, Trophy, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { hasEligibleTies, getScheduleRoundsArrays } from '../../generators/tieFormats';
import { countActiveFilters, buildRoundsWithRows } from '../lists/scheduleRows';
import { tournamentScheduleAPI } from '../../../tournaments/services/tournamentApi';
import { eventsAPI } from '../../../services/api';
import { MatchHandlers } from '../../../tournaments/utils/matchHandlers';
import SummaryBar from '../SummaryBar';
import ScheduleResultView from '../ScheduleResultView';
import MLPSettingsModal from '../MLPSettingsModal';
import CourtsSettingsModal from '../CourtsSettingsModal';
import FiltersModal from '../FiltersModal';
import FullscreenScheduleOverlay from '../FullscreenScheduleOverlay';
import Stepper from '../Stepper';

/**
 * Wizard step 4 of 4 — the generated schedule itself. MLP tie-format
 * configuration lives behind a modal (see MLPSettingsModal.jsx) triggered by
 * the "MLP Settings" button here, rather than inline in the result view —
 * that button renders whenever the schedule has a match between two real
 * 4-player team rosters, regardless of the format's structure (single elim,
 * groups, brackets, ...) or resultView. MLP eligibility is a match-level
 * property, not a structure-level one — see tieFormats.getScheduleRoundsArrays
 * for how each format's differently-shaped schedule gets normalized to check
 * this the same way. The one thing resultView still controls is whether the
 * configured slate is actually VISIBLE anywhere: bracket-tree views
 * (BracketBoard, RegionalBracketView, ...) never expand a tie's games, only
 * a List view does (see MatchListView.jsx's collapsible rows) — so on a
 * bracket-only format, switch to its List toggle to see the slate.
 *
 * "Court / Field Assignment" (see CourtsSettingsModal.jsx) is the courts/
 * fields list itself — always available, unlike MLP Settings, since it
 * applies regardless of format/participant type. Per-match/per-round court
 * reassignment and custom rules live in the gear icon on each row/round
 * instead (see MatchSettingsModal.jsx) — this button only manages the list.
 *
 * Legs (round_robin, cross_group, page_playoff) is set here, not earlier in
 * the wizard — it always starts at its default of 1, and the Stepper below
 * just edits a local draft until "Regenerate" is clicked, which calls
 * `generate({ legs })` (see useScheduleBuilder's `generate` overrides param)
 * to rebuild the schedule against the same participants/groups with the new
 * value, rather than re-walking the wizard.
 *
 * "Filters" (see FiltersModal.jsx) narrows every list this schedule renders
 * (Court/Round/Group/Player/Status/My Games, all combinable) — one global
 * applied state threaded straight through to ScheduleResultView, same
 * `filters`/`currentUserId` props every list-rendering component down the
 * tree already accepts. "Fullscreen" (see FullscreenScheduleOverlay.jsx)
 * only re-parents the schedule CARD below into a viewport-filling overlay
 * when active — the toolbar here (this button included) stays in the
 * normal page flow either way, so Filters/Court/MLP Settings remain
 * reachable without leaving fullscreen.
 */
const GeneratedScheduleStep = ({
  tournament,
  refreshLiveState,
  format,
  schedule,
  participantType,
  participantCount,
  legs,
  generate,
  viewMode,
  tieFormat,
  tieOverrides,
  matchOrderOverrides,
  config,
  updateCourts,
  roundOverrides,
  rowOverrides,
  filters,
  filterOptions,
  currentUserId,
  searchUsers,
  tournamentMatchSettings,
  tournamentVO,
  canManageThisSchedule,
  canStartTournament,
  canCompleteTournament,
  startScoring,
  openLiveGame,
  startTournament,
  completeTournament,
  isUpdatingTournamentStatus,
  tournamentStatusError,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addRound,
  addMatch,
  fetchAllParticipants,
  canSaveSchedule,
  saveSchedule,
  isSaving,
  saveError,
  lastSavedAt,
  applyFilters,
  clearFilters,
  changeViewMode,
  changeTieFormat,
  applyTieOverrides,
  reorderRow,
  applyRoundOverride,
  applyRowOverride,
  startEditing,
  reset,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [mlpModalOpen, setMlpModalOpen] = useState(false);
  const [courtsModalOpen, setCourtsModalOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingLegs, setPendingLegs] = useState(legs);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // The game currently being scored/viewed INSIDE the fullscreen overlay
  // (see FullscreenScheduleOverlay's 'game' sub-view) — { gameId,
  // gameCommonId, label, sportName, teamsData }, same shape TournamentDetailPage's
  // liveGameTabs use, just kept local instead of opening a page tab. Only
  // ever set while isFullscreen is true (see handleStartScoring/
  // handleOpenLiveGame below); staying null outside fullscreen is what keeps
  // the existing page-tab behavior completely unchanged there.
  const [fullscreenGame, setFullscreenGame] = useState(null);

  // The row Previous/Next currently has open in the read-only detail view
  // (see FullscreenScheduleOverlay's MatchDetailPanel) — browsing to a match
  // must NOT silently call startScoring (that creates a real game_common and
  // claims the current user as sole scorer as a side effect of just looking
  // around; someone using Previous/Next isn't necessarily even the scorer,
  // could just be checking other matches' details, court, timing, etc.).
  // Previous/Next always lands here regardless of the match's status —
  // actually entering scoring/viewing the live game is a separate, explicit
  // click on the panel's own Start Scoring / View Live Game button (see
  // handleDetailStartScoring/handleDetailOpenLive below).
  const [detailRow, setDetailRow] = useState(null);

  const closeFullscreenGame = useCallback(() => {
    setFullscreenGame(null);
    setDetailRow(null);
  }, []);

  // Stay in sync whenever the applied value changes from outside this
  // screen (a fresh generation after Back -> different format -> Generate).
  useEffect(() => {
    setPendingLegs(legs);
  }, [legs]);

  const roundsArrays = useMemo(() => getScheduleRoundsArrays(format, schedule), [format, schedule]);
  const mlpEligible = useMemo(() => hasEligibleTies(roundsArrays), [roundsArrays]);
  const activeFilterCount = countActiveFilters(filters);

  // Tournament lifecycle: once the tournament itself has started (or
  // finished), the schedule stops being something you EDIT — Legs, MLP
  // Settings, Court/Field Assignment, Save Schedule, and SummaryBar's
  // Back/Start-over all disappear, matching the old Matches tab's behavior.
  // Filters/Fullscreen stay — those are view tools, not editing tools.
  const tournamentStatus = tournamentVO?.status || '';
  const isLocked = tournamentStatus === 'in_progress' || tournamentStatus === 'completed';

  // Same real hydration MatchTable.jsx uses for what actually renders
  // (scheduleRows.js's buildRoundsWithRows — resolveOverrides computing
  // `.status`, `.court`, `.number`, ...), not the raw schedule.rounds[].matches[]
  // getScheduleRoundsArrays exposes. Needed for MatchDetailPanel (embedded in
  // fullscreen's Previous/Next — see detailRow above) to show real status/
  // court/labels instead of blanks, and for goToAdjacentGame below to read a
  // real `.status` instead of always-undefined. `filters` is deliberately
  // omitted (passed null) — Previous/Next through fullscreen scoring
  // shouldn't skip matches just because the Filters panel happens to be
  // narrowing the visible list right now.
  const allLiveMatches = useMemo(
    () => roundsArrays.flatMap((rounds) =>
      buildRoundsWithRows(rounds, matchOrderOverrides, config.courts, roundOverrides, rowOverrides, null, currentUserId)
        .flatMap(({ round, rows }) => rows.map((row) => ({ ...row, roundLabel: round.label })))
    ).filter((row) => row?.liveData),
    [roundsArrays, matchOrderOverrides, config.courts, roundOverrides, rowOverrides, currentUserId]
  );
  const hasSavedSchedule = allLiveMatches.length > 0;
  const unplayedGamesCount = allLiveMatches.filter(
    (match) => match.liveData.status !== 'completed' && match.liveData.checkin_status !== 'completed'
  ).length;

  // Start Scoring / open-live-game, redirected to the fullscreen overlay's
  // own embedded 'game' sub-view instead of a page tab whenever fullscreen
  // is open — outside fullscreen these are untouched (same page-tab
  // behavior as always). Passed into scheduleView below in place of the raw
  // startScoring/openLiveGame props.
  const handleStartScoring = useCallback(
    (row) => (isFullscreen ? startScoring(row, setFullscreenGame) : startScoring(row)),
    [isFullscreen, startScoring]
  );
  const handleOpenLiveGame = useCallback(
    (row) => (isFullscreen ? openLiveGame(row, setFullscreenGame) : openLiveGame(row)),
    [isFullscreen, openLiveGame]
  );

  // Previous/Next through allLiveMatches (every real match across the whole
  // schedule, in order). A live/completed target has no side effects to
  // guard against — viewing it is exactly what a row click in the list
  // itself already does directly, no detail detour — so this skips straight
  // to the game component, same as MatchRow.jsx's own canOpenLiveGame check.
  // A not-yet-started target DOES have a real side effect waiting behind it
  // (creating a game_common, claiming the current user as sole scorer), so
  // that one still lands on detailRow's confirm-first flow — same
  // "you'll be the sole scorer" gate the list's own Start Scoring pill uses,
  // just reached by Previous/Next instead of a click on that pill.
  const activeRowId = fullscreenGame?.gameId || detailRow?.liveData?.id;
  const fullscreenGameIndex = activeRowId
    ? allLiveMatches.findIndex((match) => match.liveData.id === activeRowId)
    : -1;
  const hasPrevGame = fullscreenGameIndex > 0;
  const hasNextGame = fullscreenGameIndex !== -1 && fullscreenGameIndex < allLiveMatches.length - 1;
  const goToAdjacentGame = useCallback((direction) => {
    if (fullscreenGameIndex === -1) return;
    const targetRow = allLiveMatches[fullscreenGameIndex + (direction === 'next' ? 1 : -1)];
    if (!targetRow) return;
    const liveStatus = targetRow.status || targetRow.liveData?.checkin_status || targetRow.liveData?.status;
    if (liveStatus === 'live' || liveStatus === 'completed') {
      // detailRow is deliberately NOT cleared up front here — openLiveGame is
      // async (awaits an API call before its payload callback fires), so
      // clearing it immediately would reopen the exact flicker this same
      // pattern already fixed for handleDetailOpenLive: a gap where both
      // `game` and `detailRow` are null trips FullscreenScheduleOverlay's
      // fallback-to-schedule effect. Whatever was already showing (the
      // previous game or detail view) just stays up during that gap, and
      // both pieces of state swap together, in one batch, once the real
      // payload lands.
      openLiveGame(targetRow, (payload) => {
        setFullscreenGame(payload);
        setDetailRow(null);
      });
    } else {
      setFullscreenGame(null);
      setDetailRow(targetRow);
    }
  }, [fullscreenGameIndex, allLiveMatches, openLiveGame]);

  // MatchDetailPanel's own Start Scoring (after its inline confirm step) /
  // View Live Game buttons — the only place Previous/Next-driven browsing
  // is actually allowed to call startScoring/openLiveGame, now that it's
  // behind an explicit click instead of firing just from navigating past a
  // match.
  //
  // detailRow is deliberately NOT cleared up front — startScoring/
  // openLiveGame are async (they await an API call before the payload
  // callback fires), so clearing it immediately left a real gap where both
  // `game` and `detailRow` were null at once. FullscreenScheduleOverlay
  // treats that as "nothing to show", falls back to the schedule sub-view,
  // then flips back to 'game' once the payload lands a moment later — a
  // visible flash of the real schedule list on every transition. Clearing
  // detailRow in the SAME callback that sets fullscreenGame keeps the
  // detail panel on screen (unchanged, no flicker) for that brief async
  // gap, then swaps directly to the live game in one batched update once
  // the real payload is ready.
  const handleDetailStartScoring = useCallback((row) => {
    startScoring(row, (payload) => {
      setFullscreenGame(payload);
      setDetailRow(null);
    });
  }, [startScoring]);
  const handleDetailOpenLive = useCallback((row) => {
    openLiveGame(row, (payload) => {
      setFullscreenGame(payload);
      setDetailRow(null);
    });
  }, [openLiveGame]);

  // Mirrors TournamentDetailPage's own handleGameComplete (complete the
  // game, complete its event, refresh) but refreshes this schedule's own
  // live state and returns to the schedule list instead of closing a page
  // tab — there isn't one here.
  const handleFullscreenGameComplete = useCallback(async (gameId, result) => {
    try {
      const matchHandlers = new MatchHandlers({ user });
      await matchHandlers.completeGame(gameId, result, tournament);
      const gameDetails = await tournamentScheduleAPI.getGame(gameId);
      if (gameDetails?.event_id) {
        await eventsAPI.updateStatus(gameDetails.event_id, 'completed');
      }
      await refreshLiveState();
      setFullscreenGame(null);
      toast.success('Game and event completed!');
    } catch (err) {
      console.error('[GeneratedScheduleStep] Error completing game:', err);
      toast.error(err.message || 'Failed to complete game');
    }
  }, [user, tournament, refreshLiveState]);

  const showStartPanel =
    hasSavedSchedule && !isLocked && canStartTournament && (tournamentStatus === 'draft' || tournamentStatus === 'registration_open');
  const showCompletePanel = hasSavedSchedule && canCompleteTournament && tournamentStatus !== 'completed';

  // No explicit "close on success" — a successful start/complete reloads the
  // whole page (see useScheduleBuilder's startTournament/completeTournament),
  // which unmounts this dialog anyway. On failure it stays open so
  // tournamentStatusError renders in place instead of vanishing silently.

  // Same content either way — only WHERE it renders changes (inline card vs.
  // FullscreenScheduleOverlay's portal). See that component's doc comment
  // for why this doesn't lose Schedule<->Standings state: `children` here
  // stays mounted continuously for as long as fullscreen is open.
  const scheduleView = (
    <ScheduleResultView
      format={format}
      schedule={schedule}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      tieFormat={tieFormat}
      tieOverrides={tieOverrides}
      matchOrderOverrides={matchOrderOverrides}
      reorderRow={reorderRow}
      courts={config.courts}
      sportType={config.sportType}
      tournamentMatchSettings={tournamentMatchSettings}
      roundOverrides={roundOverrides}
      rowOverrides={rowOverrides}
      applyRoundOverride={applyRoundOverride}
      applyRowOverride={applyRowOverride}
      filters={filters}
      currentUserId={currentUserId}
      onClearFilters={clearFilters}
      tournamentVO={tournamentVO}
      canManageThisSchedule={canManageThisSchedule}
      startScoring={handleStartScoring}
      openLiveGame={handleOpenLiveGame}
      updateMatchSchedule={updateMatchSchedule}
      deleteMatchRow={deleteMatchRow}
      assignMatchParticipants={assignMatchParticipants}
      // Add Round/Add Match only make sense against a schedule that's
      // actually been saved to the backend — a round's `realRoundNumber`
      // (what these send as `round_number`) doesn't exist on a freshly
      // generated, not-yet-saved preview. Withholding the callback (rather
      // than passing it and letting the gating check run anyway) is what
      // hides the buttons — see CrossGroupView/RoundSection's `!!addRound`/
      // `!!addMatch` checks.
      // Also withheld in fullscreen specifically — day-of scoring is what
      // fullscreen is for; adding a round belongs in the regular editing
      // view. `scheduleView` (this element) is the same instance reused by
      // both the non-fullscreen wrapper and FullscreenScheduleOverlay below,
      // so gating on `isFullscreen` here correctly hides it only in the one
      // that's actually mounted at a time.
      addRound={hasSavedSchedule && !isFullscreen ? addRound : undefined}
      addMatch={hasSavedSchedule ? addMatch : undefined}
      fetchAllParticipants={hasSavedSchedule ? fetchAllParticipants : undefined}
      participantType={participantType}
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1">
          <SummaryBar
            format={format}
            participantType={participantType}
            participantCount={participantCount}
            onEdit={startEditing}
            onReset={reset}
            readOnly={isLocked}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {format.supportsLegs && !isLocked && (
            <div className={`flex items-center gap-3 rounded-xl border ${theme.border} ${theme.card} px-3 py-2`}>
              <Stepper label="Legs" value={pendingLegs} min={1} max={9} onChange={setPendingLegs} />
              {pendingLegs !== legs && (
                <button
                  type="button"
                  onClick={() => generate({ legs: pendingLegs })}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
                >
                  <RefreshCw size={13} />
                  Regenerate
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => setFiltersModalOpen(true)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
          >
            <FilterIcon size={15} />
            Filters
            {activeFilterCount > 0 && (
              <span className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold ${theme.buttonPrimary}`}>
                {activeFilterCount}
              </span>
            )}
          </button>
          {!isLocked && (
            <button
              type="button"
              onClick={() => setCourtsModalOpen(true)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <MapPin size={15} />
              Court / Field Assignment
            </button>
          )}
          {mlpEligible && !isLocked && (
            <button
              type="button"
              onClick={() => setMlpModalOpen(true)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <Swords size={15} />
              MLP Settings
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
          >
            <Maximize2 size={15} />
            Fullscreen
          </button>
          {canSaveSchedule && !isLocked && (
            <button
              type="button"
              onClick={saveSchedule}
              disabled={isSaving}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99] disabled:opacity-60`}
            >
              <Save size={15} />
              {isSaving ? 'Saving…' : 'Save Schedule'}
            </button>
          )}
        </div>
      </div>

      {showStartPanel && (
        <div className={`tf-animate-in flex flex-col items-center gap-3 rounded-2xl border-2 border-blue-500 ${theme.backgroundSecondary} p-6`}>
          <div className="flex items-center gap-2">
            <Play size={22} className="text-blue-500" />
            <h3 className={`text-lg font-semibold ${theme.text}`}>Ready to Start Tournament?</h3>
          </div>
          <p className={`max-w-md text-center text-sm ${theme.textMuted}`}>
            Starting the tournament will lock registrations and prevent participants from withdrawing.
          </p>
          <button
            type="button"
            onClick={() => setShowStartDialog(true)}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
          >
            <Play size={16} />
            Start Tournament
          </button>
        </div>
      )}

      {canSaveSchedule && (saveError || lastSavedAt) && (
        <div
          className={`tf-animate-in flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium ${
            saveError ? 'border-red-500/40 bg-red-500/10 text-red-500' : `${theme.border} ${theme.card} ${theme.textMuted}`
          }`}
        >
          {saveError ? <AlertCircle size={15} /> : <Check size={15} className="text-emerald-500" />}
          {saveError || `Schedule saved — everyone viewing this tournament now sees it.`}
        </div>
      )}

      {!isFullscreen && (
        <div className={`tf-animate-in rounded-2xl border ${theme.border} ${theme.card} p-5`}>
          {scheduleView}
        </div>
      )}

      {/* Available once a schedule exists, regardless of completion status —
          some games may legitimately never get played (e.g. an MLP
          Dreambreaker), so this doesn't try to guess whether that's "fine";
          the confirm dialog just tells the user how many games are still
          unplayed and warns those can't be edited afterward. */}
      {showCompletePanel && (
        <div className={`tf-animate-in flex flex-col items-center gap-3 rounded-2xl border-2 ${theme.border} ${theme.backgroundSecondary} p-6`}>
          <div className="flex items-center gap-2">
            <Trophy size={22} className={theme.textMuted} />
            <h3 className={`text-lg font-semibold ${theme.text}`}>Complete Tournament</h3>
          </div>
          <p className={`max-w-md text-center text-sm ${theme.textMuted}`}>
            Finalize this tournament's results whenever you're ready — completed tournaments (and their matches) become read-only.
          </p>
          <button
            type="button"
            onClick={() => setShowCompleteDialog(true)}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
          >
            <Trophy size={16} />
            Complete Tournament
          </button>
        </div>
      )}

      <FullscreenScheduleOverlay
        isOpen={isFullscreen}
        onClose={() => { setIsFullscreen(false); closeFullscreenGame(); }}
        title={`${format.label} — ${participantCount} ${participantType}`}
        onRefresh={refreshLiveState}
        tournament={tournament}
        game={fullscreenGame}
        detailRow={detailRow}
        tournamentVO={tournamentVO}
        currentUserId={currentUserId}
        onDetailStartScoring={handleDetailStartScoring}
        onDetailOpenLive={handleDetailOpenLive}
        onCloseGame={closeFullscreenGame}
        onGameComplete={handleFullscreenGameComplete}
        hasPrevGame={hasPrevGame}
        hasNextGame={hasNextGame}
        onPrevGame={() => goToAdjacentGame('prev')}
        onNextGame={() => goToAdjacentGame('next')}
        onOpenFilters={() => setFiltersModalOpen(true)}
        activeFilterCount={activeFilterCount}
      >
        {scheduleView}
      </FullscreenScheduleOverlay>

      {mlpEligible && (
        <MLPSettingsModal
          isOpen={mlpModalOpen}
          onClose={() => setMlpModalOpen(false)}
          roundsArrays={roundsArrays}
          tieFormat={tieFormat}
          onTieFormatChange={changeTieFormat}
          tieOverrides={tieOverrides}
          onTieOverridesChange={applyTieOverrides}
        />
      )}

      <CourtsSettingsModal
        isOpen={courtsModalOpen}
        onClose={() => setCourtsModalOpen(false)}
        courts={config.courts}
        onCourtsChange={updateCourts}
      />

      <FiltersModal
        isOpen={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        filters={filters}
        options={filterOptions}
        courts={config.courts}
        myGamesAvailable={currentUserId != null}
        searchUsers={searchUsers}
        onApply={applyFilters}
      />

      {/* Start/Complete Tournament confirm dialogs render at z-[56] — above
          FullscreenScheduleOverlay's z-[55] (the panels that open them stay
          in normal page flow even while fullscreen is active, see that
          component's doc comment), below MatchDetailModal's z-[60]. */}
      {showStartDialog && (
        <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-lg p-6 ${theme.card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className={`flex items-center gap-2 text-lg font-bold ${theme.text}`}>
                <Play size={20} className="text-blue-500" />
                Start Tournament
              </h3>
              <button
                type="button"
                onClick={() => setShowStartDialog(false)}
                disabled={isUpdatingTournamentStatus}
                className={`rounded-lg p-1 ${theme.buttonGhost}`}
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">This action will start the tournament</p>
                    <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">Once started, the following will be locked:</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-blue-700 dark:text-blue-400">
                      <li>New participant registrations</li>
                      <li>Participant withdrawals/unenrollment</li>
                      <li>Tournament participant list</li>
                      <li>Tournament format changes</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className={`text-sm ${theme.textMuted}`}>
                Are you ready to start the tournament? Make sure all participants are registered and the schedule is finalized.
              </p>

              {tournamentStatusError && (
                <p className="text-sm font-medium text-red-500">{tournamentStatusError}</p>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowStartDialog(false)}
                  disabled={isUpdatingTournamentStatus}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${theme.border} ${theme.text} ${theme.buttonGhost} disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={startTournament}
                  disabled={isUpdatingTournamentStatus}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {isUpdatingTournamentStatus ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Starting…
                    </>
                  ) : (
                    <>
                      <Play size={14} />
                      Start Tournament
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompleteDialog && (
        <div className="fixed inset-0 z-[56] flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-lg p-6 ${theme.card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className={`flex items-center gap-2 text-lg font-bold ${theme.text}`}>
                <Trophy size={20} className="text-yellow-500" />
                Complete Tournament
              </h3>
              <button
                type="button"
                onClick={() => setShowCompleteDialog(false)}
                disabled={isUpdatingTournamentStatus}
                className={`rounded-lg p-1 ${theme.buttonGhost}`}
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">This action will finalize the tournament</p>
                    <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-400">Once completed, you won't be able to:</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-yellow-700 dark:text-yellow-400">
                      <li>Edit match results or scores</li>
                      <li>Add or delete rounds/games</li>
                      <li>Reschedule matches</li>
                      <li>Modify tournament format</li>
                    </ul>
                    {unplayedGamesCount > 0 && (
                      <p className="mt-2 text-xs font-medium text-yellow-800 dark:text-yellow-300">
                        {unplayedGamesCount} game{unplayedGamesCount === 1 ? ' is' : 's are'} still unplayed — {unplayedGamesCount === 1 ? 'it' : 'they'} will be locked as-is and can't be edited after this.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <p className={`text-sm ${theme.textMuted}`}>
                {unplayedGamesCount > 0
                  ? `Are you sure you want to complete the tournament? You won't be able to edit any of the ${unplayedGamesCount} unplayed game${unplayedGamesCount === 1 ? '' : 's'}.`
                  : 'Are you sure all results are correct and you want to complete the tournament?'}
              </p>

              {tournamentStatusError && (
                <p className="text-sm font-medium text-red-500">{tournamentStatusError}</p>
              )}

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCompleteDialog(false)}
                  disabled={isUpdatingTournamentStatus}
                  className={`rounded-lg border px-4 py-2 text-sm font-medium ${theme.border} ${theme.text} ${theme.buttonGhost} disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={completeTournament}
                  disabled={isUpdatingTournamentStatus}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {isUpdatingTournamentStatus ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Completing…
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Complete Tournament
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedScheduleStep;
